import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Table, Image, Form } from "react-bootstrap";
import moment from "moment";
import {
  incidentInitiationListFetch,
  getIncidentById,
  deleteIncidentAtInitiation,
} from "actions";
import { isEmpty } from "lodash";
import { DataTableEmptyState, DeleteModal } from "CommonComponent";
import AccessRights from "utils/AccessRights";

// Step labels
const stepLabels = [
  "Initiation",
  "Review",
  "Investigation",
  "Resolution",
  "CAPA",
  "Final Report",
];

// constants (keep these)
const DOT_SIZE = 20;
const TRACK_HEIGHT = 7;
const PADDING_X = 17;
const GREEN = "#1AAA55";
const GRAY = "#d3d3d3";

const ProgressRow = ({
  completion = {},
  name = "General Name",
  date = "N/A",
  timezone="N/A"
}) => {
  const totalSteps = stepLabels.length;
  const completedCount = stepLabels.filter(
    (s) => completion?.[s.toLowerCase()]
  ).length;

  // use segments so fill doesn't overshoot
  const totalSegments = Math.max(1, totalSteps - 1);
  const completedSegments = Math.max(
    0,
    Math.min(completedCount - 1, totalSegments)
  );
  const fillPct = (completedSegments / totalSegments) * 100;

  // dots should be vertically centered relative to track (paddingTop 12)
  const DOT_TOP_PX = 12 + TRACK_HEIGHT / 2; // matches padding-top in container

  return (
    <div className="w-100">
      {/* header */}
      <div className="font-13 font-weight-medium text-dark mb-2">
        {name} | {date} | {timezone}
      </div>

      {/* outer padded container */}
      <div
        style={{
          position: "relative",
          padding: `12px ${PADDING_X}px 28px ${PADDING_X}px`,
        }}
      >
        {/* inner wrapper: track + absolute children positioned relative to this */}
        <div style={{ position: "relative", width: "100%" }}>
          {/* base track */}
          <div
            style={{
              height: TRACK_HEIGHT,
              background: GRAY,
              borderRadius: TRACK_HEIGHT,
            }}
          />
          {/* filled track (percentage of inner wrapper width) */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: TRACK_HEIGHT,
              width: `${fillPct}%`,
              background: GREEN,
              borderRadius: TRACK_HEIGHT,
              transition: "width 300ms ease",
            }}
          />

          {/* dots positioned relative to the inner wrapper width (0%..100%) */}
          {stepLabels.map((label, i) => {
            const leftPct = (i / (totalSteps - 1)) * 100;
            const isCompleted = !!completion?.[label.toLowerCase()];

            return (
              <div
                key={label}
                title={label}
                style={{
                  position: "absolute",
                  left: `${leftPct}%`, // <- relative to inner wrapper
                  top: `3.5px`, // centered vertically (12px padding + half track)
                  transform: "translate(-50%, -50%)",
                  width: DOT_SIZE,
                  height: DOT_SIZE,
                  borderRadius: "50%",
                  background: isCompleted ? GREEN : GRAY,
                  boxShadow: "0 0 0 2px white",
                }}
              />
            );
          })}
        </div>
      </div>

      {/* labels aligned with dots */}
      <div
        style={{
          position: "relative",
          // marginTop: "20px",
          padding: `0 ${PADDING_X}px`,
        }}
      >
        {stepLabels.map((label, i) => {
          const leftPct = (i / (stepLabels.length - 1)) * 100;

          let alignmentStyle = {};
          if (i === 0) {
            alignmentStyle = { transform: "translateX(0%)", textAlign: "left" };
          } else if (i === stepLabels.length - 1) {
            alignmentStyle = {
              transform: "translateX(-100%)",
              textAlign: "right",
            };
          } else {
            alignmentStyle = {
              transform: "translateX(-50%)",
              textAlign: "center",
            };
          }

          return (
            <small
              key={i}
              style={{
                position: "absolute",
                left: `${leftPct}%`,
                fontSize: 12,
                color: "#6c757d",
                whiteSpace: "nowrap",
                ...alignmentStyle,
              }}
            >
              {label}
            </small>
          );
        })}
      </div>
    </div>
  );
};

const IncidentList = () => {
  let location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const incidentState = useSelector((state) => state.incidentState);
  const session = useSelector((state) => state.session);

  const [recordId, setRecordId] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [data, setData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchData = () => {
    if (
      session.current_page_permissions &&
      session.current_page_permissions.object_id &&
      session.current_page_permissions.page_url === location.pathname
    ) {
      dispatch(
        incidentInitiationListFetch(session.current_page_permissions.object_id)
      );
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 300000);
    return () => clearInterval(intervalId);
  }, [session.current_page_permissions]);

  useEffect(() => {
    if (incidentState.showUpdateForm) {
      navigate("/incidentInitiation/update");
    }
  }, [incidentState.showUpdateForm]);

  useEffect(() => {
    if (incidentState.showViewPage) {
      navigate("/incidentInitiation/view");
    }
  }, [incidentState.showViewPage]);

  useEffect(() => {
    if (incidentState.redirect_to_list) {
      dispatch(
        incidentInitiationListFetch(session.current_page_permissions.object_id)
      );
    }
  }, [incidentState.redirect_to_list]);

  useEffect(() => {
    if (Array.isArray(incidentState.incidentList)) {
      setData(incidentState.incidentList);
    }
  }, [incidentState.incidentList]);

  const showUpdateForm = (incidentId) => {
    dispatch(getIncidentById(incidentId, "update"));
  };

  const showViewPopup = (incidentId) => {
    dispatch(getIncidentById(incidentId, "view"));
  };

  const handleDeleteClick = (id) => {
    setRecordId(id);
    setShowDeleteModal(true);
  };

  const deleteRecord = async () => {
    await dispatch(
      deleteIncidentAtInitiation(
        recordId,
        session.current_page_permissions.object_id
      )
    );
    setShowDeleteModal(false);
    setRecordId("");
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((item) =>
      (item.incident_name || "")
        .toLowerCase()
        .includes(filterValue.toLowerCase())
    );
  }, [data, filterValue]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const sorted = useMemo(() => {
    if (!sortColumn) return filteredData;
    const copy = [...filteredData];
    copy.sort((a, b) => {
      const av = a[sortColumn]?.toString().toLowerCase() ?? "";
      const bv = b[sortColumn]?.toString().toLowerCase() ?? "";
      if (av < bv) return sortOrder === "asc" ? -1 : 1;
      if (av > bv) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredData, sortColumn, sortOrder]);

  const pageData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, currentPage, rowsPerPage]);

  return (
    <div className="overview-list">
      {/* Search */}
      <div
        className="mb-4 position-relative bg-transparent border-0 text-color font-13 font-weight-normal"
        style={{ maxWidth: "300px" }}
      >
        <input
          type="text"
          placeholder="Search here..."
          className="bg-transparent border-0 text-color font-13 font-weight-normal"
          style={{ paddingLeft: "35px" }}
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
        />
        <Image
          src="/assets/images/search-icon.svg"
          alt="Search"
          width={16}
          height={16}
          className="position-absolute"
          style={{
            top: "50%",
            left: "10px",
            transform: "translateY(-50%)",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Table */}
      {isEmpty(pageData) ? (
        <DataTableEmptyState />
      ) : (
        <Table className="mb-3">
          <tbody>
            {pageData.map(
              (item, i) => (
                console.log("item", item),
                (
                  <tr key={item.id || i} className="bordered-row-content">
                    <td>
                      <ProgressRow
                        completion={item.completion}
                        name={item.incident_name}
                        date={
                          item.incident_datetime
                            ? moment(item.incident_datetime + "Z").format(
                                "YYYY-MM-DD hh:mm A"
                              )
                            : "N/A"
                        }
                        timezone={item.incident_timezone || "N/A"}
                      />
                    </td>

                    {/* Actions */}
                    <td className="overview-actions">
                      <div>
                        {AccessRights(
                          session.current_page_permissions,
                          "view",
                          item.created_by
                        ) &&
                          item.status === "final_report" && (
                            <Image
                              src="/assets/images/view-icon.svg"
                              width={20}
                              className="me-2 cursor-pointer"
                              title="View"
                              onClick={() => showViewPopup(item.id)}
                            />
                          )}

                        {AccessRights(
                          session.current_page_permissions,
                          "edit",
                          item.created_by
                        ) &&
                          item.status === "initiation" &&
                          item.is_deleted === "n" && (
                            <Image
                              src="/assets/images/edit-icon.svg"
                              width={20}
                              className="me-2 cursor-pointer"
                              title="Edit"
                              onClick={() => showUpdateForm(item.id)}
                            />
                          )}

                        {AccessRights(
                          session.current_page_permissions,
                          "delete",
                          item.created_by
                        ) &&
                          item.status === "initiation" &&
                          item.is_deleted === "n" && (
                            <Image
                              src="/assets/images/delete-icon.svg"
                              width={20}
                              className="cursor-pointer"
                              title="Delete"
                              onClick={() => handleDeleteClick(item.id)}
                            />
                          )}
                      </div>
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </Table>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center">
        <div className="pagination">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="previous-btn-dataTable me-2 d-flex align-items-center"
          >
            <Image
              src="/assets/images/arrow-previous.svg"
              width={14}
              className="me-2 mb-1"
            />
            Previous
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={
                p === currentPage
                  ? "previous-btn-dataTable-active mx-1"
                  : "previous-btn-dataTable mx-1"
              }
              onClick={() => setCurrentPage(p)}
            >
              {p}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="previous-btn-dataTable ms-2"
          >
            Next
            <Image
              src="/assets/images/arrow-next.svg"
              width={14}
              className="ms-2 mb-1"
            />
          </button>
        </div>

        <Form.Select
          value={rowsPerPage}
          onChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
          style={{ width: 90 }}
        >
          {[5, 10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Form.Select>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        onDelete={deleteRecord}
        handleClose={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

export default IncidentList;
